"use client";

import { useAppSelector } from "@/store/hooks";
import Image from "next/image";
import type React from "react";
import type { HeroProps } from "@/types/hero.types";
import HeroButtons from "./HeroButtons";
import HeroContent from "./HeroContent";
import HeroQuickInfoComponent from "./HeroQuickInfo";
import HeroScrollIndicator from "./HeroScrollIndicator";

const Hero: React.FC<HeroProps> = ({
  primaryButtonText: propPrimaryButtonText = "View Our Menu",
  secondaryButtonText: propSecondaryButtonText = "Make a Reservation",
  quickInfo: propQuickInfo = [],
  backgroundImage: propBackgroundImage = "/hero-bg.jpg",
  backgroundVideo: propBackgroundVideo = "/hero-bg.mp4",
  onPrimaryClick,
  onSecondaryClick,
  className,
}) => {
  const { data } = useAppSelector((state) => state.content);
  const heroContent = data?.hero;

  const primaryButtonText =
    heroContent?.primaryButtonText || propPrimaryButtonText;
  const secondaryButtonText =
    heroContent?.secondaryButtonText || propSecondaryButtonText;
  const quickInfo = heroContent?.quickInfo || propQuickInfo;
  const backgroundImage = heroContent?.backgroundImage || propBackgroundImage;
  const backgroundVideo = heroContent?.backgroundVideo || propBackgroundVideo;

  return (
    <section
      className={`relative min-h-screen flex items-center justify-center overflow-hidden ${
        className || ""
      }`}
    >
      {/* Background Video with Overlay */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        >
          <source src={backgroundVideo} type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          <Image
            src={backgroundImage}
            alt="Restaurant interior"
            fill
            className="object-cover"
          />
        </video>
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      {/* Decorative Elements */}
      <div className="fixed top-16 right-6 w-6 h-6 bg-black/10 rounded-full"></div>
      <div className="fixed top-64 left-8 w-3 h-3 bg-black/15 rounded-full"></div>
      <div className="fixed bottom-32 right-10 w-4 h-4 bg-black/12 rounded-full"></div>
      <div className="fixed top-1/3 right-1/4 w-2 h-2 bg-black/8 rounded-full"></div>

      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
        {/* Main Content Card */}
        <div className="bg-white/20 border border-white/30 rounded-md p-12 md:p-16 backdrop-blur-sm">
          <HeroContent />

          <HeroButtons
            primaryButtonText={primaryButtonText}
            secondaryButtonText={secondaryButtonText}
            onPrimaryClick={onPrimaryClick}
            onSecondaryClick={onSecondaryClick}
          />

          <HeroQuickInfoComponent quickInfo={quickInfo} />
        </div>
      </div>

      <HeroScrollIndicator />
    </section>
  );
};

export default Hero;
